import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { KeyRound, Smartphone } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { handleApiError } from '@/lib/api/axios'
import { credentialToServerFormat, isWebAuthnSupported, registerEnterprisePasskey } from '@/lib/security/webauthn-enterprise'
import { sittingApi } from '../api'

type Step = 'code' | 'password' | 'device' | 'totp' | 'passkey' | 'waiting'

/**
 * /mobilise: a standby user called in for a recovery sets themselves up in one sitting
 * (MFA build plan, Stage 4).
 *
 *   1. their address and the code that was texted to them;
 *   2. a new password;
 *   3. a sign-in device: this computer or phone's fingerprint or face unlock, a security key,
 *      or an authenticator app.
 *
 * It ends on "waiting for your team lead" - not signed in. Nothing here is a session: the
 * sitting's token lives in this page's memory, and closing the page means starting again with
 * the same code. The account opens when the lead confirms them, and they are told by text.
 */
export function MobilisePage() {
    const { t } = useTranslation()
    const [step, setStep] = useState<Step>('code')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const token = useRef<string | null>(null)

    const [email, setEmail] = useState('')
    const [code, setCode] = useState('')
    const [password, setPassword] = useState('')
    const [again, setAgain] = useState('')
    const [deviceName, setDeviceName] = useState('')
    const [totp, setTotp] = useState<{ secret: string; qrCode: string; setupTicket: string } | null>(null)
    const [totpCode, setTotpCode] = useState('')

    const run = async (work: () => Promise<void>) => {
        setBusy(true)
        setError(null)
        try {
            await work()
        } catch (e) {
            const status = (e as { response?: { status?: number } })?.response?.status
            setError(handleApiError(e))
            // The sitting has run out or been closed: only the code step can go on from here.
            if (status === 401 && step !== 'code') { token.current = null; setStep('code') }
        } finally {
            setBusy(false)
        }
    }
    const codeComplete = code.replace(/[\s-]/g, '').length === 12
    const passwordsMatch = password.length > 0 && password === again

    const start = () => run(async () => {
        const sitting = await sittingApi.start(email.trim(), code)
        token.current = sitting.sittingToken
        setStep(sitting.passwordSet ? 'device' : 'password')
    })
    const savePassword = () => run(async () => {
        await sittingApi.password(token.current!, password)
        setPassword(''); setAgain('')
        setStep('device')
    })
    const beginTotp = () => run(async () => {
        setTotp(await sittingApi.totpBegin(token.current!))
        setStep('totp')
    })
    const confirmTotp = () => run(async () => {
        await sittingApi.totpConfirm(token.current!, { code: totpCode, setupTicket: totp!.setupTicket, name: deviceName.trim() || undefined })
        token.current = null
        setStep('waiting')
    })
    const addPasskey = () => run(async () => {
        const options = await sittingApi.passkeyBegin(token.current!)
        const { credential, deviceName: detected } = await registerEnterprisePasskey(options as never, deviceName.trim() || undefined)
        await sittingApi.passkeyComplete(token.current!, { credential: credentialToServerFormat(credential), name: deviceName.trim() || detected })
        token.current = null
        setStep('waiting')
    })
    // Leaving the page ends the sitting: nothing of it is kept.
    useEffect(() => () => { token.current = null }, [])

    const title = t(`mobilise.${step}.title`)
    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="w-full max-w-md space-y-6">
                <div className="flex justify-end"><LanguageSwitcher /></div>
                <div className="flex justify-center"><Logo className="h-16" /></div>
                <Card data-testid="mobilise-page" data-step={step}>
                    <CardHeader>
                        <CardTitle>{title}</CardTitle>
                        <CardDescription>{t(`mobilise.${step}.description`)}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {step === 'code' && (
                            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (email.trim() && codeComplete) start() }}>
                                <div className="space-y-2">
                                    <Label htmlFor="mobilise-email">{t('mobilise.code.emailLabel')}</Label>
                                    <Input id="mobilise-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="mobilise-code">{t('mobilise.code.codeLabel')}</Label>
                                    <Input id="mobilise-code" value={code} autoComplete="one-time-code" autoCapitalize="characters" spellCheck={false} maxLength={20}
                                        placeholder="XXXX-XXXX-XXXX" className="font-mono tracking-widest" onChange={(e) => setCode(e.target.value.toUpperCase())} />
                                </div>
                                <Button type="submit" className="w-full" disabled={busy || !email.trim() || !codeComplete}>{t('mobilise.continue')}</Button>
                            </form>
                        )}

                        {step === 'password' && (
                            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (passwordsMatch) savePassword() }}>
                                <div className="space-y-2">
                                    <Label htmlFor="mobilise-password">{t('mobilise.password.newLabel')}</Label>
                                    <Input id="mobilise-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="mobilise-password-again">{t('mobilise.password.againLabel')}</Label>
                                    <Input id="mobilise-password-again" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
                                </div>
                                <p className="text-xs text-muted-foreground">{t('mobilise.password.rules')}</p>
                                <Button type="submit" className="w-full" disabled={busy || !passwordsMatch}>{t('mobilise.continue')}</Button>
                            </form>
                        )}

                        {step === 'device' && (
                            <div className="space-y-3">
                                <div className="space-y-2">
                                    <Label htmlFor="mobilise-device-name">{t('devices.nameLabel')}</Label>
                                    <Input id="mobilise-device-name" value={deviceName} maxLength={120} onChange={(e) => setDeviceName(e.target.value)} />
                                </div>
                                {isWebAuthnSupported() && (
                                    <Button className="w-full justify-start" onClick={() => { setStep('passkey'); addPasskey() }} disabled={busy}>
                                        <KeyRound className="mr-2 h-4 w-4" />{t('signInDevice.passkey')}
                                    </Button>
                                )}
                                <Button variant={isWebAuthnSupported() ? 'outline' : 'default'} className="w-full justify-start" onClick={beginTotp} disabled={busy}>
                                    <Smartphone className="mr-2 h-4 w-4" />{t('signInDevice.totp')}
                                </Button>
                                <p className="text-xs text-muted-foreground">{t(isWebAuthnSupported() ? 'signInDevice.passkeyHint' : 'signInDevice.totpHint')}</p>
                            </div>
                        )}

                        {step === 'passkey' && (
                            <div className="space-y-3">
                                <Button className="w-full" onClick={addPasskey} disabled={busy}>{t('mobilise.passkey.tryAgain')}</Button>
                                <Button variant="ghost" className="w-full" onClick={() => setStep('device')} disabled={busy}>{t('mobilise.back')}</Button>
                            </div>
                        )}

                        {step === 'totp' && totp && (
                            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (/^\d{6}$/.test(totpCode)) confirmTotp() }}>
                                <div className="flex justify-center"><img src={totp.qrCode} alt={t('devices.totp.qrAlt')} className="h-48 w-48" /></div>
                                <p className="break-all text-center font-mono text-xs text-muted-foreground">{totp.secret}</p>
                                <div className="space-y-2">
                                    <Label htmlFor="mobilise-totp">{t('devices.totp.codeLabel')}</Label>
                                    <Input id="mobilise-totp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))} />
                                </div>
                                <Button type="submit" className="w-full" disabled={busy || !/^\d{6}$/.test(totpCode)}>{t('mobilise.continue')}</Button>
                                <Button type="button" variant="ghost" className="w-full" onClick={() => setStep('device')} disabled={busy}>{t('mobilise.back')}</Button>
                            </form>
                        )}

                        {step === 'waiting' && (
                            <div className="space-y-3" data-testid="mobilise-waiting">
                                <p className="text-sm">{t('mobilise.waiting.next')}</p>
                                <Button asChild variant="outline" className="w-full"><Link to="/login">{t('mobilise.waiting.toSignIn')}</Link></Button>
                            </div>
                        )}

                        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
